import { useEffect, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { formatoCantidad } from '@/lib/utils/formato-precio';

export interface PestanaDeNavegacion {
  texto: string;
  href: string;
  activa: boolean;
  /** El contador al lado del nombre. Sin número, nada. */
  cuenta?: number | null;
  icono?: LucideIcon;
}

/**
 * Pestañas en línea que son ENLACES (rediseño del 18-oct, artefacto): cada una
 * es otra pantalla con su dirección —Mi empresa › Usuarios, Roles, Equipos…—,
 * así se puede compartir el enlace y el menú lateral marca la que está abierta.
 * Se ven como las pestañas de Compite: el nombre y una raya bajo la activa.
 */
export function PestanasDeNavegacion({
  pestanas,
  etiqueta,
  className,
}: {
  pestanas: PestanaDeNavegacion[];
  /** Para el lector de pantalla: qué se elige con estas pestañas. */
  etiqueta: string;
  className?: string;
}) {
  // En el teléfono, la activa a la vista: Configuración es la última y quedaba fuera.
  const nav = useRef<HTMLElement>(null);
  const activa = pestanas.find((p) => p.activa)?.href;
  useEffect(() => {
    const el = nav.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!el || !nav.current || nav.current.scrollWidth <= nav.current.clientWidth) return;
    nav.current.scrollLeft = el.offsetLeft - (nav.current.clientWidth - el.offsetWidth) / 2;
  }, [activa]);

  return (
    <nav
      ref={nav}
      aria-label={etiqueta}
      className={cn(
        // Una sola fila que se desliza en el teléfono: partidas en dos líneas, la
        // raya de la activa quedaba en el medio de la lista.
        'relative flex items-end gap-x-6 overflow-x-auto overflow-y-hidden border-b border-border [scrollbar-width:none] sm:gap-x-8 [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {pestanas.map((p) => {
        const Icono = p.icono;
        return (
          <Link
            key={p.href}
            to={p.href}
            aria-current={p.activa ? 'page' : undefined}
            className={cn(
              '-mb-px inline-flex h-12 shrink-0 items-center gap-2 whitespace-nowrap border-b-[3px] px-0.5 text-[15px] transition-colors duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              p.activa
                ? 'border-prixia-sky font-semibold text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {Icono && <Icono className="h-4 w-4" aria-hidden="true" />}
            {p.texto}
            {p.cuenta != null && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
                {formatoCantidad(p.cuenta)}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
